import {
	CodeBlock,
	Endpoint,
	Alert,
	ParamList,
	Param,
	Table,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function ApiGames() {
	return (
		<div className={styles.docsPage}>
			<h1>Game API Reference</h1>
			<p className={styles.lead}>
				The Game API allows authenticated players to place bets, check game
				state, and verify provably fair outcomes. All endpoints require a valid
				session token obtained through the Operator API.
			</p>

			<Alert variant="info" title="Authentication">
				All Game API endpoints require a valid session token in the
				Authorization header:{' '}
				<code>Authorization: Bearer {'<sessionToken>'}</code>
			</Alert>

			{/* Dice Game */}
			<h2>Dice Game</h2>
			<p>
				The classic crypto dice game where players predict whether a roll will
				be above or below a target number. Features adjustable win probability
				from 1% to 98%.
			</p>

			{/* Place Dice Bet */}
			<h3 id="dice-bet">Place Dice Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/game/dice/bet"
				description="Places a bet on the dice game. The outcome is determined using provably fair RNG."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="amount" type="string" required>
					Bet amount in the smallest currency unit (e.g., cents, satoshis). Must
					be within the table limits.
				</Param>
				<Param name="target" type="number" required>
					Target number between 0.01 and 99.99. This is the threshold for
					winning.
				</Param>
				<Param name="condition" type="string" required>
					Win condition: <code>under</code> (roll must be less than target) or{' '}
					<code>over</code> (roll must be greater than target).
				</Param>
				<Param name="clientSeed" type="string">
					Custom client seed for provably fair verification. If not provided,
					uses the session's current client seed.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/game/dice/bet \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "1000",
    "target": 50.00,
    "condition": "under",
    "clientSeed": "my_custom_seed_123"
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "betId": "bet_dice_abc123xyz",
    "game": "dice",
    "roll": 42.35,
    "target": 50.00,
    "condition": "under",
    "won": true,
    "amount": "1000",
    "payout": "1980",
    "multiplier": 1.98,
    "profit": "980",
    "balanceBefore": "50000",
    "balanceAfter": "50980",
    "provablyFair": {
      "serverSeedHash": "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
      "clientSeed": "my_custom_seed_123",
      "nonce": 42
    },
    "createdAt": "2024-01-15T14:22:15Z"
  }
}`}
			</CodeBlock>

			<h4>Multiplier Calculation</h4>
			<p>
				The dice multiplier is calculated based on win probability with a 1%
				house edge:
			</p>
			<CodeBlock language="javascript" title="Multiplier Formula">
				{`// For "under" bets:
winProbability = target / 100
multiplier = (1 - houseEdge) / winProbability
// Example: target 50, multiplier = 0.99 / 0.50 = 1.98x

// For "over" bets:
winProbability = (100 - target) / 100
multiplier = (1 - houseEdge) / winProbability
// Example: target 50, multiplier = 0.99 / 0.50 = 1.98x`}
			</CodeBlock>

			{/* Plinko Game */}
			<h2>Plinko Game</h2>
			<p>
				Drop a ball through a pyramid of pegs to land in multiplier buckets.
				Features configurable rows (8-16) and risk levels that affect multiplier
				distribution.
			</p>

			{/* Place Plinko Bet */}
			<h3 id="plinko-bet">Place Plinko Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/game/plinko/bet"
				description="Drops a ball in the plinko board. The path and landing bucket are determined using provably fair RNG."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="amount" type="string" required>
					Bet amount in the smallest currency unit.
				</Param>
				<Param name="rows" type="number" required>
					Number of peg rows: <code>8</code>, <code>10</code>, <code>12</code>,{' '}
					<code>14</code>, or <code>16</code>. More rows means more buckets and
					variance.
				</Param>
				<Param name="riskLevel" type="string" required>
					Risk level affecting multiplier distribution: <code>low</code>,{' '}
					<code>medium</code>, or <code>high</code>. Higher risk means larger
					potential wins but lower probability.
				</Param>
				<Param name="clientSeed" type="string">
					Custom client seed for provably fair verification.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/game/plinko/bet \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "1000",
    "rows": 12,
    "riskLevel": "medium"
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "betId": "bet_plinko_def456uvw",
    "game": "plinko",
    "rows": 12,
    "riskLevel": "medium",
    "path": [1, 0, 1, 1, 0, 0, 1, 0, 1, 1, 0, 1],
    "bucket": 7,
    "multiplier": 1.4,
    "won": true,
    "amount": "1000",
    "payout": "1400",
    "profit": "400",
    "balanceBefore": "50980",
    "balanceAfter": "51380",
    "provablyFair": {
      "serverSeedHash": "a1b2c3d4e5f6...",
      "clientSeed": "default_seed",
      "nonce": 43
    },
    "createdAt": "2024-01-15T14:25:30Z"
  }
}`}
			</CodeBlock>

			<h4>Plinko Multipliers (12 rows, medium risk)</h4>
			<Table
				columns={[
					{ key: 'bucket', header: 'Bucket' },
					{ key: 'multiplier', header: 'Multiplier' },
					{ key: 'probability', header: 'Probability' },
				]}
				data={[
					{ bucket: '0 (edge)', multiplier: '33x', probability: '0.024%' },
					{ bucket: '1', multiplier: '11x', probability: '0.29%' },
					{ bucket: '2', multiplier: '4x', probability: '1.61%' },
					{ bucket: '3', multiplier: '2x', probability: '5.37%' },
					{ bucket: '4', multiplier: '1.1x', probability: '12.08%' },
					{ bucket: '5', multiplier: '0.6x', probability: '19.34%' },
					{ bucket: '6 (center)', multiplier: '0.3x', probability: '22.56%' },
					{ bucket: '7', multiplier: '0.6x', probability: '19.34%' },
					{ bucket: '8', multiplier: '1.1x', probability: '12.08%' },
					{ bucket: '9', multiplier: '2x', probability: '5.37%' },
					{ bucket: '10', multiplier: '4x', probability: '1.61%' },
					{ bucket: '11', multiplier: '11x', probability: '0.29%' },
					{ bucket: '12 (edge)', multiplier: '33x', probability: '0.024%' },
				]}
			/>

			{/* Coinflip Game */}
			<h2>Coinflip Game</h2>
			<p>
				A simple heads or tails game with a fixed 50% win probability and 1.98x
				multiplier.
			</p>

			{/* Place Coinflip Bet */}
			<h3 id="coinflip-bet">Place Coinflip Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/game/coinflip/bet"
				description="Places a bet predicting heads or tails. The result is determined using provably fair RNG."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="amount" type="string" required>
					Bet amount in the smallest currency unit.
				</Param>
				<Param name="prediction" type="string" required>
					Your prediction: <code>heads</code> or <code>tails</code>.
				</Param>
				<Param name="clientSeed" type="string">
					Custom client seed for provably fair verification.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/game/coinflip/bet \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "5000",
    "prediction": "heads"
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "betId": "bet_flip_ghi789rst",
    "game": "coinflip",
    "prediction": "heads",
    "result": "heads",
    "won": true,
    "amount": "5000",
    "payout": "9900",
    "multiplier": 1.98,
    "profit": "4900",
    "balanceBefore": "51380",
    "balanceAfter": "56280",
    "provablyFair": {
      "serverSeedHash": "d4e5f6a7b8c9...",
      "clientSeed": "default_seed",
      "nonce": 44
    },
    "createdAt": "2024-01-15T14:28:45Z"
  }
}`}
			</CodeBlock>

			{/* Crash Game */}
			<h2>Crash Game</h2>
			<p>
				A multiplayer game where a multiplier increases from 1x until it crashes
				at a random point. Players must cash out before the crash to win.
			</p>

			<Alert variant="warning" title="Multiplayer Game">
				Crash is a multiplayer game with shared rounds. Players join lobbies and
				bet together on the same crash point. The game flow differs from
				single-player games.
			</Alert>

			{/* Join Crash Lobby */}
			<h3 id="crash-join">Join Crash Lobby</h3>
			<Endpoint
				method="POST"
				path="/api/v1/game/crash/join"
				description="Joins the current crash game lobby. Returns the lobby state and WebSocket connection details for real-time updates."
			/>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/game/crash/join \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "lobbyId": "crash_lobby_main",
    "roundId": "round_crash_001",
    "status": "betting",
    "bettingEndsIn": 5200,
    "players": 24,
    "totalWagered": "125000",
    "websocket": {
      "url": "wss://api.rngfort.com/ws/crash",
      "token": "ws_token_xyz..."
    },
    "previousRounds": [
      { "roundId": "round_crash_000", "crashPoint": 2.45 },
      { "roundId": "round_crash_-001", "crashPoint": 1.12 },
      { "roundId": "round_crash_-002", "crashPoint": 8.73 }
    ]
  }
}`}
			</CodeBlock>

			{/* Place Crash Bet */}
			<h3 id="crash-bet">Place Crash Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/game/crash/bet"
				description="Places a bet on the current crash round. Must be called during the betting phase before the round starts."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="amount" type="string" required>
					Bet amount in the smallest currency unit.
				</Param>
				<Param name="autoCashout" type="number">
					Optional automatic cashout multiplier. If the game reaches this
					multiplier, your bet is automatically cashed out. Must be at least
					1.01.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/game/crash/bet \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "2000",
    "autoCashout": 2.5
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "betId": "bet_crash_jkl012mno",
    "roundId": "round_crash_001",
    "amount": "2000",
    "autoCashout": 2.5,
    "status": "pending",
    "balanceAfter": "54280",
    "createdAt": "2024-01-15T14:30:00Z"
  }
}`}
			</CodeBlock>

			{/* Cashout Crash Bet */}
			<h3 id="crash-cashout">Cash Out Crash Bet</h3>
			<Endpoint
				method="POST"
				path="/api/v1/game/crash/cashout"
				description="Cashes out your bet at the current multiplier. Must be called while the round is in progress and before the crash."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="betId" type="string" required>
					The bet ID to cash out.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/game/crash/cashout \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "betId": "bet_crash_jkl012mno"
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "betId": "bet_crash_jkl012mno",
    "roundId": "round_crash_001",
    "status": "cashed_out",
    "amount": "2000",
    "cashoutMultiplier": 1.85,
    "payout": "3700",
    "profit": "1700",
    "balanceAfter": "57980",
    "cashedOutAt": "2024-01-15T14:30:45Z"
  }
}`}
			</CodeBlock>

			<Alert variant="error" title="Timing Critical">
				Cashout requests are time-sensitive. If the crash occurs before your
				request is processed, your bet will be lost. Network latency can affect
				the outcome.
			</Alert>

			{/* History & Verification */}
			<h2>History & Verification</h2>
			<p>
				Retrieve bet history and verify the fairness of any bet using provably
				fair cryptography.
			</p>

			{/* Get Bet History */}
			<h3 id="bet-history">Get Bet History</h3>
			<Endpoint
				method="GET"
				path="/api/v1/game/history"
				description="Retrieves the betting history for the current session or player."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="game" type="string">
					Filter by game: <code>dice</code>, <code>plinko</code>,{' '}
					<code>coinflip</code>, <code>crash</code>.
				</Param>
				<Param name="won" type="boolean">
					Filter by outcome: <code>true</code> for wins, <code>false</code> for
					losses.
				</Param>
				<Param name="limit" type="number">
					Number of results (1-100, default: 20).
				</Param>
				<Param name="cursor" type="string">
					Pagination cursor from previous response.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/game/history?game=dice&limit=10" \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..."`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "bets": [
      {
        "betId": "bet_dice_abc123xyz",
        "game": "dice",
        "amount": "1000",
        "payout": "1980",
        "multiplier": 1.98,
        "won": true,
        "details": {
          "roll": 42.35,
          "target": 50.00,
          "condition": "under"
        },
        "provablyFair": {
          "serverSeedHash": "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
          "clientSeed": "my_custom_seed_123",
          "nonce": 42,
          "verified": false
        },
        "createdAt": "2024-01-15T14:22:15Z"
      }
    ],
    "pagination": {
      "hasMore": true,
      "cursor": "eyJsYXN0SWQiOiJiZXRfZGljZV9hYmMxMjN4eXoifQ=="
    },
    "stats": {
      "totalBets": 125,
      "totalWagered": "250000",
      "totalWon": "245000",
      "netProfit": "-5000"
    }
  }
}`}
			</CodeBlock>

			{/* Verify Bet */}
			<h3 id="verify-bet">Verify Bet Fairness</h3>
			<Endpoint
				method="GET"
				path="/api/v1/game/verify/:betId"
				description="Reveals the server seed and provides all data needed to independently verify a bet's outcome."
			/>

			<h4>Path Parameters</h4>
			<ParamList>
				<Param name="betId" type="string" required>
					The unique bet identifier to verify.
				</Param>
			</ParamList>

			<Alert variant="warning" title="Seed Rotation">
				Verifying a bet reveals the server seed used for that bet. For security,
				the server seed is automatically rotated after verification, and a new
				seed hash is generated for future bets.
			</Alert>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET https://api.rngfort.com/api/v1/game/verify/bet_dice_abc123xyz \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..."`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "betId": "bet_dice_abc123xyz",
    "game": "dice",
    "verification": {
      "serverSeed": "7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069",
      "serverSeedHash": "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
      "clientSeed": "my_custom_seed_123",
      "nonce": 42,
      "combinedHash": "a1b2c3d4e5f6g7h8i9j0...",
      "rawResult": "4235",
      "result": 42.35
    },
    "algorithm": {
      "name": "HMAC-SHA256",
      "steps": [
        "1. Concatenate serverSeed + clientSeed + nonce",
        "2. Generate HMAC-SHA256 hash",
        "3. Take first 8 hex characters",
        "4. Convert to decimal and scale to game range"
      ],
      "verificationCode": "const crypto = require('crypto');\\nconst hash = crypto.createHmac('sha256', serverSeed).update(clientSeed + ':' + nonce).digest('hex');\\nconst result = parseInt(hash.substring(0, 8), 16) % 10000 / 100;"
    },
    "verified": true,
    "outcome": {
      "roll": 42.35,
      "target": 50.00,
      "condition": "under",
      "won": true
    }
  }
}`}
			</CodeBlock>

			{/* WebSocket Events */}
			<h2>WebSocket Events (Crash Game)</h2>
			<p>
				The crash game uses WebSocket for real-time updates. Connect using the
				URL and token provided by the join endpoint.
			</p>

			<h3>Event Types</h3>
			<CodeBlock language="json" title="round_start">
				{`{
  "event": "round_start",
  "data": {
    "roundId": "round_crash_002",
    "startTime": 1704067200,
    "serverSeedHash": "abc123..."
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="multiplier_update">
				{`{
  "event": "multiplier_update",
  "data": {
    "roundId": "round_crash_002",
    "multiplier": 1.45,
    "elapsed": 3200
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="player_cashout">
				{`{
  "event": "player_cashout",
  "data": {
    "roundId": "round_crash_002",
    "playerId": "player_xxx",
    "multiplier": 1.85,
    "payout": "3700"
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="round_crash">
				{`{
  "event": "round_crash",
  "data": {
    "roundId": "round_crash_002",
    "crashPoint": 2.45,
    "serverSeed": "revealed_seed...",
    "nextRoundIn": 5000
  }
}`}
			</CodeBlock>

			{/* Rate Limits */}
			<h2>Rate Limits</h2>
			<Table
				columns={[
					{ key: 'endpoint', header: 'Endpoint' },
					{ key: 'limit', header: 'Rate Limit' },
					{ key: 'window', header: 'Window' },
				]}
				data={[
					{
						endpoint: 'Dice/Plinko/Coinflip Bets',
						limit: '100 requests',
						window: 'Per minute',
					},
					{
						endpoint: 'Crash Bet/Cashout',
						limit: '30 requests',
						window: 'Per minute',
					},
					{
						endpoint: 'History/Verify',
						limit: '60 requests',
						window: 'Per minute',
					},
				]}
			/>

			{/* Next Steps */}
			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/provably-fair">Provably Fair</a> - Learn how
						verification works
					</li>
					<li>
						<a href="/docs/games-dice">Dice Game Guide</a> - Deep dive into dice
						mechanics
					</li>
					<li>
						<a href="/docs/games-plinko">Plinko Game Guide</a> - Understand
						plinko strategies
					</li>
					<li>
						<a href="/docs/games-crash">Crash Game Guide</a> - Master the crash
						game
					</li>
					<li>
						<a href="/docs/errors">Error Codes</a> - Handle game errors
						gracefully
					</li>
				</ul>
			</div>
		</div>
	);
}
